// Feature-flag registry — the mechanism that makes trunk-based development work.
// Unfinished work merges to main behind a flag that defaults to OFF, instead of
// living on a long-lived branch that drifts.
//
// Lifecycle: add the flag here (default false) → merge and ship it dark → flip the
// default to true when it is ready → DELETE the flag and its dead code. Flags are
// meant to be short-lived; a stale flag is worse than the branch it replaced.
//
// QA on a PR preview can flip a flag without a rebuild:
//   ?ff=newSummary        → on
//   ?ff=-newSummary       → off
//   ?ff=a,-b              → both, comma-separated
// The choice is persisted to localStorage so it survives in-app navigation.

const KEY = 'fg:flags'

/** Defaults, per flag. Anything not listed here is treated as OFF. */
export const FLAGS: Record<string, boolean> = {
    // newSummary: false,
}

/** `"a,-b"` → `{ a: true, b: false }`. Blank / malformed entries are ignored. */
export function parseFlagParam(raw: string): Record<string, boolean> {
    const out: Record<string, boolean> = {}
    for (const part of raw.split(',')) {
        const name = part.trim().replace(/^-/, '')
        if (name) out[name] = !part.trim().startsWith('-')
    }
    return out
}

const readOverrides = (): Record<string, boolean> => {
    try {
        return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, boolean>
    } catch {
        return {}
    }
}

/** Merge any `?ff=` overrides into storage. Call once, early in boot. */
export function initFlags(): void {
    const raw = new URLSearchParams(window.location.search).get('ff')
    if (!raw) return
    localStorage.setItem(KEY, JSON.stringify({ ...readOverrides(), ...parseFlagParam(raw) }))
}

/** Effective value: a stored override wins over the registry default. */
export const isEnabled = (name: string): boolean => readOverrides()[name] ?? FLAGS[name] ?? false

export const setFlag = (name: string, on: boolean): void => {
    localStorage.setItem(KEY, JSON.stringify({ ...readOverrides(), [name]: on }))
}

export const resetFlags = (): void => localStorage.removeItem(KEY)
